import { containsMath, stripMathDelimiters } from "@ever-tutor/et-ui";

import type { TrailSegment } from "@/features/tutor/types/chatTrail";

// Anchored on a single run: /^\$[\s\S]*\$$/ also matched "$a$ + $b$", which
// spans two runs, and returned it unwrapped.
const DELIMITED = /^\$[^$]*\$$/;

export const wrapMathRun = (latex: string): string => {
  const trimmed = latex.trim();
  if (!trimmed) return "";

  return DELIMITED.test(trimmed) ? trimmed : `$${trimmed}$`;
};

const MATH_RUN = /\$[^$\n]+\$|\\\([\s\S]*?\\\)|\\\[[\s\S]*?\\\]/g;

// `$…$` is the one ambiguous form: it is also how prices are written, and the
// regex spans the gap between two of them ("$9.99, tax $"). A run earns maths
// treatment if it carries a LaTeX metacharacter, or if its body is a single
// unbroken token — which is what the composer emits and what a price is not.
//
// The cost is hand-typed maths with a space and no metacharacter: "$x = 5$"
// stays prose. Deliberate — mangling a price is worse than not typesetting a
// run the composer never produces.
const isMathRun = (run: string): boolean => {
  if (!run.startsWith("$")) return true;
  return containsMath(run) || !/\s/.test(run.slice(1, -1));
};

export const splitMathSegments = (text: string): TrailSegment[] => {
  const segments: TrailSegment[] = [];
  let last = 0;

  const flushProse = (upTo: number) => {
    if (upTo > last)
      segments.push({ text: text.slice(last, upTo), isMath: false });
  };

  // Rewinds one character on rejection rather than resuming past the run: the
  // closing "$" a price borrowed is the opening "$" of whatever follows it, as
  // in "if the price is $20, find $x$".
  MATH_RUN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = MATH_RUN.exec(text)) !== null) {
    const start = match.index;
    if (!isMathRun(match[0])) {
      MATH_RUN.lastIndex = start + 1;
      continue;
    }
    flushProse(start);
    segments.push({ text: match[0], isMath: true });
    last = start + match[0].length;
  }

  flushProse(text.length);
  return segments;
};

export const isWholeMathRun = (text: string): boolean =>
  stripMathDelimiters(text.trim()) !== null;
